import { ArrowRight, Award, Clock, Shield, Instagram, Mail } from "lucide-react";
import { AnimatedSection } from "@/components/ui/AnimatedSection";

// Constants for better maintainability
const ABOUT_CONFIG = {
  phone: "554599700570",
  whatsappMessage: "Olá, gostaria de agendar uma consulta.",
  social: {
    instagram: "https://www.instagram.com/adv.deborabertolinifracaro",
    email: "deborabfracaro.adv@gmail.com"
  },
  colors: {
    primary: "#BFA15C",
    whatsapp: "#25D366",
    whatsappHover: "#20BD5C",
    instagram: {
      from: "#833AB4",
      via: "#C13584", 
      to: "#E1306C"
    },
    gmail: "#EA4335",
    gmailHover: "#D93025"
  },
  images: {
    aboutBottom: "/images/aboutinferior.png",
    meeting: "/images/sentadacompc.jpg",
    portrait: "/images/cortada.JPG"
  }
} as const;

// Reusable data for features
const FEATURES = [
  {
    icon: Award,
    title: "Excelência",
    description: "Compromisso com resultados",
    mobileDescription: "Foco em resultados"
  },
  {
    icon: Clock,
    title: "Experiência", 
    description: "Expertise consolidada",
    mobileDescription: "Expertise consolidada"
  },
  {
    icon: Shield,
    title: "Confiança",
    description: "Relações duradouras",
    mobileDescription: "Relações duradouras"
  }
] as const;

// Reusable component for feature cards
const FeatureCard = ({ 
  icon: Icon, 
  title, 
  description, 
  isMobile = false 
}: { 
  icon: typeof Award; 
  title: string; 
  description: string; 
  isMobile?: boolean;
}) => (
  <div className="text-center p-4">
    <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-[#BFA15C]/10 flex items-center justify-center">
      <Icon className="h-6 w-6 text-[#BFA15C]" />
    </div>
    <h4 className="font-medium mb-1">{title}</h4>
    <p className="text-sm text-gray-500 text-center">{description}</p>
  </div>
);

// Reusable component for contact buttons
const ContactButton = ({ 
  type, 
  onClick, 
  children 
}: { 
  type: 'whatsapp' | 'instagram' | 'email'; 
  onClick: () => void; 
  children: React.ReactNode;
}) => {
  const getButtonClasses = () => {
    const baseClasses = "group relative inline-flex items-center justify-center gap-1 px-2 py-3 md:px-6 md:py-3 text-white rounded-full hover:shadow-lg transition-all font-medium overflow-hidden transform hover:-translate-y-0.5 text-xs md:text-base whitespace-nowrap min-w-[90px] md:min-w-[140px]";
    
    switch (type) {
      case 'whatsapp':
        return `${baseClasses} bg-[#25D366] hover:bg-[#20BD5C]`;
      case 'instagram':
        return `${baseClasses} bg-gradient-to-r from-[#833AB4] via-[#C13584] to-[#E1306C] hover:from-[#C13584] hover:via-[#E1306C] hover:to-[#833AB4]`;
      case 'email':
        return `${baseClasses} bg-[#EA4335] hover:bg-[#D93025]`;
      default:
        return baseClasses;
    }
  };

  return (
    <button 
      onClick={onClick}
      className={getButtonClasses()}
      aria-label={`Contato via ${type}`}
    >
      <span className="relative z-10 flex items-center gap-1">
        {children}
      </span>
      {type === 'instagram' && (
        <div className="absolute inset-0 bg-gradient-to-r from-[#833AB4] via-[#C13584] to-[#E1306C] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      )}
    </button>
  );
};

// Reusable component for image with optimized loading
const OptimizedImage = ({ 
  src, 
  alt, 
  className, 
  priority = false 
}: { 
  src: string; 
  alt: string; 
  className: string; 
  priority?: boolean;
}) => (
  <img 
    src={src}
    alt={alt}
    className={className}
    loading={priority ? "eager" : "lazy"}
    decoding="async"
    {...(priority && { fetchPriority: "high" })}
  />
);

// Reusable component for section header
const SectionHeader = ({ 
  label, 
  title 
}: { 
  label: string; 
  title: string;
}) => (
  <>
    <div className="flex items-center gap-3 mb-5">
      <div className={`h-px w-12 bg-[${ABOUT_CONFIG.colors.primary}]`} />
      <h3 className={`text-[${ABOUT_CONFIG.colors.primary}] font-medium uppercase text-sm`}>{label}</h3>
    </div>
    <h2 className="text-4xl md:text-5xl font-serif font-medium mb-6 leading-tight">
      {title}
    </h2>
  </>
);

const AboutSection = () => {
  const handleWhatsAppClick = () => {
    const whatsappUrl = `https://wa.me/${ABOUT_CONFIG.phone}?text=${encodeURIComponent(ABOUT_CONFIG.whatsappMessage)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleInstagramClick = () => {
    window.open(ABOUT_CONFIG.social.instagram, '_blank');
  };

  const handleEmailClick = () => {
    window.location.href = `mailto:${ABOUT_CONFIG.social.email}`;
  };

  const WhatsAppIcon = () => (
    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
    </svg>
  );

  return (
    <section 
      id="about" 
      className="section-padding pb-2 bg-gray-50"
      aria-label="Sobre a advogada Débora Fracaro"
    >
      <div className="container-custom">
        <AnimatedSection>
          <div className="flex flex-col lg:flex-row gap-12 items-start">
            {/* MOBILE: Ordem mesclada */}
            <div className="block lg:hidden w-full">
              <SectionHeader label="SOBRE" title="Minha Trajetória no Direito" />
              
              <p className="text-lawblack-600 mb-6 text-justify">
                Sou Débora Bertolini Fracaro, advogada regularmente inscrita na OAB/PR sob o nº 101.765. 
                Iniciei minha trajetória no Direito em 2015, com formação concluída em 2019 e aprovação na OAB ainda 
                durante a graduação.
                Desde então, venho atuando de forma independente, sempre pautada na ética, na responsabilidade e na busca 
                por constante aprimoramento. Em março de 2024, fundei o escritório Fracaro Advocacia, com o objetivo de 
                oferecer serviços jurídicos de qualidade e comprometimento.
              </p>
              
              {/* Imagem horizontal */}
              <div className="rounded-2xl overflow-hidden mb-6">
                <OptimizedImage
                  src={ABOUT_CONFIG.images.aboutBottom}
                  alt="Advogada Débora Fracaro em ambiente profissional"
                  className="w-full h-[200px] object-cover"
                />
              </div>
              
              <p className="text-lawblack-600 mb-6 text-justify">
                Possuo quatro pós-graduações concluídas nas áreas de Direito do Trabalho com ênfase em auxílio acidentário, 
                Direito Previdenciário, Direito Penal e Processo Penal, e Direito de Família e Sucessões. Atualmente, 
                curso mais cinco especializações: Direito Civil e Processo Civil, Direito Médico e da Saúde, Execução Penal,  
                Direito das Mulheres e medicina legal, além de diversos cursos complementares.
              </p>
              
              {/* Imagens verticais lado a lado */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="rounded-2xl overflow-hidden">
                  <OptimizedImage
                    src={ABOUT_CONFIG.images.meeting}
                    alt="Advogada em reunião de trabalho"
                    className="w-full h-[200px] object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden">
                  <OptimizedImage
                    src={ABOUT_CONFIG.images.portrait}
                    alt="Retrato profissional da advogada"
                    className="w-full h-[200px] object-cover"
                  />
                </div>
              </div>
              
              {/* Bloco de ícones */}
              <div className="grid grid-cols-3 gap-4 mb-8" role="list">
                {FEATURES.map((feature, index) => (
                  <div key={feature.title} role="listitem">
                    <FeatureCard 
                      icon={feature.icon}
                      title={feature.title}
                      description={feature.mobileDescription || feature.description}
                      isMobile={true}
                    />
                  </div>
                ))}
              </div>
              
              {/* Contatos */}
              <div className="space-y-4 mb-8">
                <div className="flex justify-center items-center gap-3"> 
                  <h3 className={`text-[${ABOUT_CONFIG.colors.primary}] font-medium uppercase text-sm text-center`}>
                    ENTRE EM CONTATO
                  </h3>
                </div>
                <div className="flex items-center gap-3 w-full flex-wrap lg:flex-nowrap overflow-x-auto">
                  <ContactButton type="whatsapp" onClick={handleWhatsAppClick}>
                    <WhatsAppIcon />
                    WhatsApp
                  </ContactButton>
                  
                  <ContactButton type="instagram" onClick={handleInstagramClick}>
                    <Instagram className="w-4 h-4" />
                    Instagram
                  </ContactButton>
                  
                  <ContactButton type="email" onClick={handleEmailClick}>
                    <Mail className="w-4 h-4" />
                    <span className="whitespace-nowrap">E-mail</span>
                  </ContactButton>
                </div>
              </div>
            </div>
            {/* DESKTOP: Layout original */}
            <div className="hidden lg:flex lg:w-1/2 flex-col gap-4">
              {/* Top row with two vertical images */}
              <div className="grid grid-cols-2 gap-6">
                <div className="rounded-2xl overflow-hidden">
                  <OptimizedImage
                    src={ABOUT_CONFIG.images.meeting}
                    alt="Advogada em reunião de trabalho"
                    className="w-full h-[447px] object-cover"
                    priority={true}
                  />
                </div>
                <div className="rounded-2xl overflow-hidden">
                  <OptimizedImage
                    src={ABOUT_CONFIG.images.portrait}
                    alt="Retrato profissional da advogada"
                    className="w-full h-[447px] object-cover"
                  />
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden w-full">
                <OptimizedImage
                  src={ABOUT_CONFIG.images.aboutBottom}
                  alt="Advogada Débora Fracaro em ambiente profissional"
                  className="w-full h-[280px] object-cover"
                />
              </div>
            </div>
            
            <div className="hidden lg:block w-full max-w-2xl mx-auto">
              <SectionHeader label="SOBRE" title="Minha Trajetória no Direito" />
              
              <p className="text-lawblack-600 mb-6 text-justify">
                Sou Débora Bertolini Fracaro, advogada regularmente inscrita na OAB/PR sob o nº 101.765. 
                Iniciei minha trajetória no Direito em 2015, com formação concluída em 2019 e aprovação na OAB ainda 
                durante a graduação.
                Desde então, venho atuando de forma independente, sempre pautada na ética, na responsabilidade e na busca 
                por constante aprimoramento. Em março de 2024, fundei o escritório Fracaro Advocacia, com o objetivo de 
                oferecer serviços jurídicos de qualidade e comprometimento.
              </p>
              
              <p className="text-lawblack-600 mb-8 text-justify">
                Possuo quatro pós-graduações concluídas nas áreas de Direito do Trabalho com ênfase em auxílio acidentário, 
                Direito Previdenciário, Direito Penal e Processo Penal, e Direito de Família e Sucessões. Atualmente, 
                curso mais cinco especializações: Direito Civil e Processo Civil, Direito Médico e da Saúde, Execução Penal,  
                Direito das Mulheres e medicina legal, além de diversos cursos complementares.
              </p>
              
              <div className="grid grid-cols-3 gap-4 mb-8" role="list">
                {FEATURES.map((feature) => (
                  <div key={feature.title} role="listitem">
                    <FeatureCard 
                      icon={feature.icon}
                      title={feature.title}
                      description={feature.description}
                    />
                  </div>
                ))}
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className={`h-px w-12 bg-[${ABOUT_CONFIG.colors.primary}]`} />
                  <h3 className={`text-[${ABOUT_CONFIG.colors.primary}] font-medium uppercase text-sm`}>
                    ENTRE EM CONTATO
                  </h3>
                </div>

                <div className="flex items-center gap-3 w-full">
                  <ContactButton type="whatsapp" onClick={handleWhatsAppClick}>
                    <WhatsAppIcon />
                    WhatsApp
                  </ContactButton>

                  <ContactButton type="instagram" onClick={handleInstagramClick}>
                    <Instagram className="w-4 h-4" />
                    Instagram
                  </ContactButton>

                  <ContactButton type="email" onClick={handleEmailClick}>
                    <Mail className="w-4 h-4" />
                    <span className="whitespace-nowrap">E-mail</span>
                  </ContactButton>
                </div>
              </div>
            </div>
          </div>

          {/* Quote section - Now full width and centered */}
          <div className="mt-12">
            <div className={`p-8 border-l-4 border-[${ABOUT_CONFIG.colors.primary}] bg-white rounded shadow-sm`}>
              <blockquote className="text-lawblack-800 italic text-lg text-center">
                "O escritório nasce com a missão de acolher e representar cada cliente com empatia, 
                excelência técnica e responsabilidade, oferecendo soluções jurídicas 
                sempre pautadas na ética e no respeito à dignidade humana."
              </blockquote>
              
              <div className="mt-6 flex items-center justify-center gap-3">
                <div className={`h-12 w-12 rounded-full overflow-hidden bg-[${ABOUT_CONFIG.colors.primary}]/10 flex items-center justify-center`}>
                  <img 
                    src="/images/novahero.JPG"
                    alt="Débora B. Fracaro"
                    className="w-full h-full object-cover rounded-full"
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    style={{ imageRendering: '-webkit-optimize-contrast' }}
                  />
                </div>
                <div>
                  <p className="font-medium">Débora B. Fracaro</p>
                  <p className="text-sm text-lawblack-500">Advogada</p>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default AboutSection;
